/**
 * AuthProvider - MSAL Authentication Provider
 * 
 * Wraps the application with MSAL authentication context
 * Handles authentication flow and provides auth state to children
 */

import React, { useEffect, useState } from 'react';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from '../config/authConfig';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import LoadingScreen from './LoadingScreen';
import UnprofiledScreen from './UnprofiledScreen';
import Logo from './Logo';
import { useStorageAuth } from '../services/storageContext';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL instance
let msalInitialized = false;
let msalInitPromise = null;

const initializeMsal = async () => {
  if (msalInitPromise) {
    return msalInitPromise;
  }
  
  if (!msalInitialized) {
    msalInitPromise = (async () => {
      try {
        await msalInstance.initialize();
        msalInitialized = true;
        console.log('MSAL initialized successfully');
        
        // Small delay to ensure everything is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Handle redirect promise after initialization
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          console.log('MSAL redirect response:', response);
        }
        
        // Add event callbacks after initialization
        msalInstance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS) {
            console.log('Login successful:', event);
          }
          
          if (event.eventType === EventType.LOGIN_FAILURE) {
            console.error('Login failed:', event);
          }
          
          if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
            console.log('Token acquired successfully:', event);
          }
          
          if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
            console.error('Token acquisition failed:', event);
          }
        });
        
        return true;
      } catch (error) {
        console.error('MSAL initialization failed:', error);
        throw error;
      }
    })();
  }
  
  return msalInitPromise;
};

// Initialize MSAL on module load
initializeMsal().catch(console.error);

/**
 * Auth content wrapper that handles authentication states
 */
const AuthContent = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Check if this is a redirect from Azure AD
  const urlParams = new URLSearchParams(window.location.search);
  const isRedirectFromAzure = urlParams.has('code') || urlParams.has('error');

  // Show loading screen while checking authentication or processing redirect
  if (authLoading || isRedirectFromAzure) {
    const message = isRedirectFromAzure 
      ? "Processing authentication..." 
      : "Checking authentication...";
    return <LoadingScreen message={message} />;
  }

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  // User is authenticated, now check permissions
  return <AuthenticatedContent>{children}</AuthenticatedContent>;
};

/**
 * Content for authenticated users - handles profile loading and permissions
 */
const AuthenticatedContent = ({ children }) => {
  const { getAccessToken } = useAuth();
  const { isLoading: permissionsLoading, isUnprofiled, error, profile } = usePermissions();
  const updateStorageAuth = useStorageAuth();
  
  // Inject auth token getter into storage service factory
  React.useEffect(() => {
    if (getAccessToken) {
      updateStorageAuth(getAccessToken);
    }
  }, [getAccessToken, updateStorageAuth]);

  // Show loading screen while loading user profile
  if (permissionsLoading) {
    return <LoadingScreen message="Loading user profile..." />;
  }

  // Debug: Log profile state
  console.log('AuthenticatedContent - Profile state:', {
    isLoading: permissionsLoading,
    isUnprofiled,
    error,
    hasProfile: !!profile,
    profileRoles: profile?.roles
  });

  // Show error if permissions check failed
  if (error) {
    // If the error is "Authentication required", redirect to login
    if (error === 'Authentication required') {
      console.log('AuthenticatedContent: Authentication required error detected, showing login screen');
      return <LoginScreen />;
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Error
            </h2>
            <p className="text-gray-600 mb-4">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show unprofiled screen for users awaiting approval
  if (isUnprofiled) {
    return <UnprofiledScreen />;
  }

  return children;
};

/**
 * Main AuthProvider component
 */
const AuthProvider = ({ children }) => {
  const [isMsalReady, setIsMsalReady] = useState(false);

  useEffect(() => {
    // Wait for MSAL to be initialized before using it
    const checkMsalInitialization = async () => {
      try {
        // Ensure MSAL is initialized
        await initializeMsal();
        
        // Handle account selection on startup
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
        
        setIsMsalReady(true);
      } catch (error) {
        console.error('Error initializing MSAL:', error);
        setIsMsalReady(true); // Set to true anyway to avoid infinite loading
      }
    };

    checkMsalInitialization();
  }, []);

  // Show loading while MSAL is initializing
  if (!isMsalReady) {
    return <LoadingScreen message="Initializing authentication..." />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContent>
        {children}
      </AuthContent>
    </MsalProvider>
  );
};

/**
 * Login screen for unauthenticated users
 */
const LoginScreen = () => {
  const handleLogin = () => {
    msalInstance.loginRedirect({
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      prompt: 'select_account'
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--tct-blue-dark)' }}>
      <div className="max-w-md w-full bg-white shadow-xl rounded-lg p-8">
        <div className="text-center">
          {/* Logo */}
          <div className="mb-8">
            <Logo size="lg" variant="horizontal" noLink className="justify-center" />
            <p className="mt-4 text-2xl font-semibold text-gray-700">
              TCT AI
            </p>
          </div>

          {/* Login form */}
          <div className="space-y-6">
            <button
              onClick={handleLogin}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Sign in with Microsoft
            </button>

            <div className="text-xs text-gray-500">
              By signing in, you agree to The Config Team's acceptable use policy.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthProvider; 